import { Box, Stack, Typography, Divider, Paper } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import { CreateProjectDto, ProjectDto } from 'tdc-web-backend/projects/schemas';
import { CompanyAdminDto, CompanyDto } from 'tdc-web-backend/companies/schemas';
import { useEffect, useState } from 'react';
import { EnumContractType, mapContractType } from 'tdc-web-backend/enums/enums';
import LoadingButton from '../../../../../components/button/LoadingButton';
import ControlledTextInput from '../../../../../components/custom-inputs/ControlledTextInput';
import useGetMany from '../../../../../utils/hooks/crud-hooks/useGetMany';
import useCreate from '../../../../../utils/hooks/crud-hooks/useCreate';
import CustomInput from '../../../../../components/custom-input/CustomInput';
import ConfirmModal from '../../../../../components/confirm-modal/ConfirmModal';
import { secondaryPink } from '../../../../../utils/color';
import { ReactComponent as ErrorAlertIcon } from '../../../../../assets/icons/layout-icons/ErrorAlertIcon.svg';
import CustomInputSelect from '../../../../../components/custom-input/inputs/CustomInputSelect';
import CustomInputAutocomplete from '../../../../../components/custom-input/inputs/CustomInputAutocomplete';
import CustomInputText from '../../../../../components/custom-input/inputs/CustomInputText';

const AdminCreateProject = () => {
  const navigate = useNavigate();
  const formMethods = useForm<CreateProjectDto>({
    defaultValues: {
      name: '',
      description: '',
      contractType: EnumContractType.Default,
      buyer: '',
      seller: '',
      requestedBy: '',
    } as CreateProjectDto,
  });
  const [isErrModalOpen, setIsErrModalOpen] = useState(false);
  const [errModalMsg, setErrModalMsg] = useState('');

  const formMethodsCheckBox = useForm({
    defaultValues: {
      requested_by_buyer: false,
      requested_by_seller: false,
    },
  });

  const { data: sellerData } = useGetMany<CompanyAdminDto>({
    resource: '/companies',
  });
  const sellers = sellerData?.data.results;
  const sellerSelected = formMethods.watch('seller');
  const sellerSelectedContractType =
    sellerSelected !== ''
      ? mapContractType.get((sellerSelected as unknown as CompanyAdminDto).defaultContractType)
      : '';

  const { data: buyerData } = useGetMany<CompanyAdminDto>({
    resource: '/companies',
  });
  const buyers = buyerData?.data.results;
  const buyerSelected = formMethods.watch('buyer');
  const buyerSelectedContractType =
    buyerSelected !== ''
      ? mapContractType.get((buyerSelected as unknown as CompanyAdminDto).defaultContractType)
      : '';

  const { mutate, isLoading } = useCreate<ProjectDto, CreateProjectDto>({ resource: '/projects' });

  const { handleSubmit } = formMethods;

  const submitForm = (data: CreateProjectDto) => {
    let requestedBy = null;
    if (formMethodsCheckBox.getValues('requested_by_buyer')) {
      requestedBy = (data.buyer as unknown as CompanyAdminDto).id;
    } else if (formMethodsCheckBox.getValues('requested_by_seller')) {
      requestedBy = (data.seller as unknown as CompanyAdminDto).id;
    }
    const postData = {
      name: data.name,
      description: data.description,
      seller: (data.seller as unknown as CompanyAdminDto).id,
      buyer: (data.buyer as unknown as CompanyAdminDto).id,
      contractType: data.contractType,
      ...(requestedBy ? { requestedBy } : {}),
    };

    mutate(postData, {
      onSuccess: (data) => {
        navigate('../projects');
      },
      onError: (err) => {
        setErrModalMsg((err as AxiosError).response?.data.message || 'Something went wrong');
        setIsErrModalOpen(true);
      },
    });
  };

  useEffect(() => {
    const subscription = formMethodsCheckBox.watch((value, e) => {
      if (e.type === 'change' && e.name === 'requested_by_buyer' && value.requested_by_buyer) {
        formMethodsCheckBox.setValue('requested_by_seller', false);
      }
      if (e.type === 'change' && e.name === 'requested_by_seller' && value.requested_by_seller) {
        formMethodsCheckBox.setValue('requested_by_buyer', false);
      }
    });
    return () => subscription.unsubscribe();
  }, [formMethodsCheckBox.watch]);

  return (
    <>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(submitForm)}>
          <Stack spacing={2} direction="row" sx={{ py: 3 }}>
            <Stack spacing={3} width="100%">
              <Typography variant="h3">Create Project</Typography>
              <Stack spacing={3}>
                <Box>
                  <Divider />
                </Box>
                <Paper
                  sx={{
                    p: 3,
                    bgcolor: 'grey.100',
                  }}
                >
                  <Stack spacing={1}>
                    <Stack spacing={6}>
                      <Stack spacing={1} sx={{ px: 5 }}>
                        <Box sx={{ mb: 0 }}>
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            Title
                          </Typography>
                          <Typography variant="caption">
                            The title should include the necessery keywords and be between 4-9 words
                            long.
                          </Typography>
                        </Box>
                        <CustomInput
                          input={<CustomInputText />}
                          name="name"
                          placeholder="Title"
                          fullWidth
                          required
                        />
                      </Stack>
                      <Stack spacing={1} sx={{ px: 5 }}>
                        <Box sx={{ mb: 0 }}>
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            Buyer
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={5} width="100%" alignItems="center">
                          <CustomInput
                            input={<CustomInputAutocomplete />}
                            name="buyer"
                            choices={buyers}
                            labelKey="name"
                            valueKey="id"
                            keepOnlyValueKeys={false}
                            placeholder="Select"
                            rules={{ required: 'Buyer is required' }}
                            fullWidth
                            sx={{ minHeight: 'none', width: '300px', mt: 2 }}
                            disableClearable
                          />
                          <FormProvider {...formMethodsCheckBox}>
                            <CustomInput
                              input={<CustomInput.Checkbox />}
                              type="text"
                              name="requested_by_buyer"
                              label="Buyer requested the project"
                              disabled={isLoading}
                            />
                          </FormProvider>
                        </Stack>
                      </Stack>
                      <Stack spacing={1} sx={{ px: 5 }}>
                        <Box sx={{ mb: 0 }}>
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            Seller
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={5} width="100%" alignItems="center">
                          <CustomInput
                            input={<CustomInputAutocomplete />}
                            name="seller"
                            choices={sellers}
                            labelKey="name"
                            valueKey="id"
                            keepOnlyValueKeys={false}
                            placeholder="Select"
                            rules={{ required: 'Seller is required' }}
                            fullWidth
                            sx={{ minHeight: 'none', width: '300px', mt: 2 }}
                            disableClearable
                          />
                          <FormProvider {...formMethodsCheckBox}>
                            <CustomInput
                              input={<CustomInput.Checkbox />}
                              type="text"
                              name="requested_by_seller"
                              label="Seller requested the project"
                              disabled={isLoading}
                            />
                          </FormProvider>
                        </Stack>
                      </Stack>
                      <Stack spacing={1} sx={{ px: 5 }}>
                        <Box sx={{ mb: 0 }}>
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            Description
                          </Typography>
                        </Box>
                        <ControlledTextInput name="description" multiline minRows={4} required />
                      </Stack>
                      <Stack spacing={2} sx={{ px: 5 }}>
                        <Box sx={{ pb: 2 }}>
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            Contract type
                          </Typography>
                        </Box>
                        <Stack spacing={3}>
                          <CustomInput
                            input={<CustomInputSelect />}
                            name="contractType"
                            choices={mapContractType}
                            label="Project contract type"
                            placeholder="Select the project contract type"
                            rules={{ required: 'Contract type is required' }}
                            sx={{ minHeight: 'none', maxWidth: '600px', mt: 2 }}
                          />
                          <Stack spacing={1}>
                            <Stack direction="row" spacing={2}>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                * Buyer defautl contract type:
                              </Typography>
                              <Typography variant="body2" sx={{}}>
                                {buyerSelectedContractType}
                              </Typography>
                            </Stack>
                            <Stack direction="row" spacing={2}>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                * Seller defautl contract type:
                              </Typography>
                              <Typography variant="body2" sx={{}}>
                                {sellerSelectedContractType}
                              </Typography>
                            </Stack>
                          </Stack>
                        </Stack>
                      </Stack>
                    </Stack>
                  </Stack>
                </Paper>

                <Divider sx={{ pt: 5 }} />

                <LoadingButton
                  onButtonClick={() => null}
                  sx={{ alignSelf: 'end' }}
                  variant="contained"
                  size="medium"
                  fontWeight="bold"
                  minWidth={102}
                  type="submit"
                  loading={isLoading}
                >
                  Save
                </LoadingButton>
              </Stack>
            </Stack>
          </Stack>
        </form>
      </FormProvider>
      <ConfirmModal
        isModalOpen={isErrModalOpen}
        setIsModalOpen={setIsErrModalOpen}
        title="Error"
        message={
          <Typography color="primaryDark.500" lineHeight="21px">
            {errModalMsg}
          </Typography>
        }
        confirmButtonText="Close"
        // onConfirm={() => navigate('../project-requests')}
        iconProps={{
          icon: <ErrorAlertIcon fill={secondaryPink[700]} style={{ width: 24, height: 24 }} />,
          wrapperBackgroundColor: secondaryPink[100],
        }}
        showCancelButton={false}
      />
    </>
  );
};

export default AdminCreateProject;
